/* PAGINA 7 · WALLET MULTI-CATENA
 *
 * Questa è l'unica schermata che mostra denaro vero su catena. Le regole che
 * l'hanno scritta, in ordine:
 *
 * 1. Dal wallet escono solo l'indirizzo pubblico TRONCATO e i saldi. Mai la
 *    chiave, nemmeno cifrata: `GET /wallets` la restituirebbe (non ha
 *    `@Exclude()`), quindi qui si chiedono esplicitamente i soli campi
 *    pubblici e la chiave non arriva nemmeno al browser. Per lo stesso motivo
 *    NON si usa `GET /wallets/:id`, che la porterebbe con sé.
 * 2. Niente segreti nei log, in localStorage o in query string. L'unica cosa
 *    che finisce in un URL è l'id numerico del wallet. L'unico momento in cui
 *    una chiave privata attraversa questa schermata è la CREAZIONE di un
 *    wallet: viaggia nel corpo del POST, il server la cifra prima di
 *    scriverla, e il campo si svuota subito. Il perché la cifratura stia lì e
 *    non nel browser è scritto in testa a `wallet/ModuloWallet.tsx`.
 * 3. Le azioni che rendono i fondi irraggiungibili stanno IN FONDO, a due
 *    tocchi, e ripetono nella conferma quanto c'è dentro e su quale indirizzo.
 * 4. Una catena che non risponde si mostra: nasconderla farebbe leggere il
 *    totale come completo, e su quel totale si decide se un bot può partire.
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Avvertenza, Distintivo, Pulsante, Scheda } from '../componenti';
import { AzioneDistruttiva } from '../componenti/AzioneDistruttiva';
import { IntestazionePagina } from '../componenti/Guscio';
import { Modale, Avvisi, usaAvvisi } from '../componenti/Sovrapposizioni';
import { ContenitoreDati, ScheletroLista, StatoStantio } from '../componenti/Stati';
import { wallets } from '../api/risorse';
import type { RiepilogoSaldoWallet } from '../api/tipi';
import { eStantio, usaRichiesta } from '../api/usaRichiesta';
import { daQuanto, ora, valuta } from '../lib/formato';
import { StrisciaSchede, type VoceScheda } from '../componenti/StrisciaSchede';
import { ModuloNuovoWallet, ModuloRinominaWallet } from './wallet/ModuloWallet';
import { SchedaCatena } from './wallet/SchedaCatena';
import { Transazioni, RicaricaTransazioni } from './wallet/Transazioni';
import { ControlloLiquidita } from './wallet/ControlloLiquidita';
import { BotDelWallet } from './wallet/BotDelWallet';
import {
  CAMPI_WALLET_PUBBLICI,
  copiaIndirizzo,
  ordinaCatene,
  soloDatiPubblici,
  troncaIndirizzo,
  type WalletPubblico,
} from './wallet/dati';
import './wallet/wallet.css';

/** Il backend rifiuta oltre 25 id per richiesta: ogni wallet costa quattro
 *  scansioni su RPC pubblici. Il taglio va detto, non subito in silenzio. */
const MAX_RIEPILOGO = 25;

export function Wallet() {
  const { id } = useParams<{ id: string }>();
  const idNumerico = id != null ? Number(id) : NaN;
  return Number.isInteger(idNumerico) ? <DettaglioWallet id={idNumerico} /> : <ElencoWallet />;
}

/* ==========================================================================
 * Elenco: quale wallet, e quanto c'è dentro
 * ======================================================================== */

function ElencoWallet() {
  const naviga = useNavigate();
  const { avvisi, mostra } = usaAvvisi();
  const [apreAggiunta, setApreAggiunta] = useState(false);

  /* Il wallet appena creato di cui il server NON ha ricavato l'indirizzo. Non
     è un dettaglio estetico: senza indirizzo non si leggono saldi e nessun bot
     può operarci, e l'unica causa possibile su una riga NUOVA è una chiave che
     non è una chiave. Resta a schermo finché non si ricarica, perché un avviso
     che sparisce da solo qui costerebbe un wallet inutile lasciato in lista. */
  const [senzaIndirizzo, setSenzaIndirizzo] = useState<string | null>(null);

  const elenco = usaRichiesta(
    () => wallets.elenco({ fields: CAMPI_WALLET_PUBBLICI, sort: ['id,ASC'] }),
    [],
  );

  const lista: WalletPubblico[] = useMemo(
    () => (elenco.dati?.data ?? []).map(soloDatiPubblici),
    [elenco.dati],
  );

  const ids = useMemo(() => lista.slice(0, MAX_RIEPILOGO).map((w) => w.id), [lista]);
  const chiaveIds = ids.join(',');

  /* Il riepilogo è una richiesta a parte: se le catene sono lente, l'elenco dei
     wallet compare comunque e i totali arrivano dopo. */
  const riepilogo = usaRichiesta(
    (segnale) => wallets.riepilogoSaldi({ ids }, segnale),
    [chiaveIds],
    { attiva: ids.length > 0 },
  );

  const perId = useMemo(() => {
    const m = new Map<number, RiepilogoSaldoWallet>();
    for (const r of riepilogo.dati ?? []) m.set(r.walletId, r);
    return m;
  }, [riepilogo.dati]);

  return (
    <div className="sb-cornice">
      <Avvisi avvisi={avvisi} />

      <IntestazionePagina
        titolo="Wallet"
        sotto={
          <span className="sb-nota">
            {lista.length === 0 ? 'nessun wallet' : `${lista.length} wallet · saldi su BNB Chain, Ethereum, Polygon e Optimism`}
          </span>
        }
        azioni={
          <Pulsante variante="primario" onClick={() => setApreAggiunta(true)}>
            Aggiungi
          </Pulsante>
        }
      />

      <div style={{ padding: 'var(--sb-pad-pagina)' }} className="sb-colonna">
        {senzaIndirizzo && (
          <Avvertenza livello="grave">
            Il wallet «{senzaIndirizzo}» è stato creato, ma il server non è riuscito a ricavarne
            l'indirizzo pubblico: la chiave inserita non è una chiave privata né una frase
            mnemonica valida. Così com'è non mostrerà saldi e nessun bot potrà operarci. Eliminalo e
            rifallo con la chiave giusta.
          </Avvertenza>
        )}

        {lista.length > MAX_RIEPILOGO && (
          <Avvertenza livello="cautela">
            I totali sono letti solo per i primi {MAX_RIEPILOGO} wallet: oltre quel numero il
            backend rifiuta la richiesta. Gli altri si aprono uno per uno.
          </Avvertenza>
        )}

        <ContenitoreDati
          stato={elenco.stato}
          causaVuoto="Non c'è nessun wallet registrato: nessun bot può operare su catena finché non ce n'è almeno uno."
          azioneVuoto={{ etichetta: 'Aggiungi il primo', onClick: () => setApreAggiunta(true) }}
          messaggioErrore={elenco.errore?.message}
          onRiprova={elenco.ricarica}
          scheletro={<ScheletroLista righe={3} />}
        >
          {lista.map((w) => {
            const r = perId.get(w.id);
            return (
              <button
                key={w.id}
                type="button"
                className="sb-w-scelta"
                onClick={() => naviga(`/wallet/${w.id}`)}
              >
                <div className="sb-riga" style={{ gap: 'var(--sb-s-4)' }}>
                  <span style={{ flex: 1, minWidth: 120, fontWeight: 'var(--sb-peso-forte)', fontSize: 'var(--sb-t-135)' }}>
                    {w.nome}
                  </span>
                  <span className="sb-w-catena__usd">
                    {r?.totalUsd == null ? (riepilogo.stato === 'caricamento' ? '…' : 'n/d') : valuta(r.totalUsd, '$', 2)}
                  </span>
                </div>

                <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
                  {troncaIndirizzo(w.indirizzo)}
                </div>

                <div className="sb-riga" style={{ gap: 'var(--sb-s-3)', marginTop: 'var(--sb-s-4)' }}>
                  {r && (
                    <Distintivo tono={r.okChains === r.totalChains ? 'neutro' : 'cautela'}>
                      {r.okChains}/{r.totalChains} catene lette
                    </Distintivo>
                  )}
                  {r && r.chainsWithGasAlert.length > 0 && (
                    <Distintivo tono="cautela">
                      gas basso su {r.chainsWithGasAlert.length}{' '}
                      {r.chainsWithGasAlert.length === 1 ? 'catena' : 'catene'}
                    </Distintivo>
                  )}
                  {r?.scannedAt && (
                    <span className="sb-nota">
                      letto {daQuanto(r.scannedAt)}
                      {r.cached ? ' · dalla cache' : ''}
                    </span>
                  )}
                  {r?.error && <span className="sb-nota" style={{ color: 'var(--sb-cautela-testo)' }}>{r.error}</span>}
                </div>
              </button>
            );
          })}
        </ContenitoreDati>
      </div>

      <ModuloNuovoWallet
        aperto={apreAggiunta}
        onChiudi={() => setApreAggiunta(false)}
        nomiEsistenti={lista.map((w) => w.nome)}
        onCreato={(w) => {
          setApreAggiunta(false);
          setSenzaIndirizzo(w.indirizzo == null ? w.nome : null);
          mostra(`Wallet "${w.nome}" creato.`, 'riuscito');
          elenco.ricarica();
        }}
      />
    </div>
  );
}

/* ==========================================================================
 * Dettaglio: le catene, le transazioni, il controllo di liquidità
 * ======================================================================== */

function DettaglioWallet({ id }: { id: number }) {
  const naviga = useNavigate();
  const { avvisi, mostra } = usaAvvisi();
  const [catenaScelta, setCatenaScelta] = useState<string>('tutte');
  const [rilettura, setRilettura] = useState(0);
  const [chiedeEliminazione, setChiedeEliminazione] = useState(false);
  const [inEliminazione, setInEliminazione] = useState(false);
  const [chiedeRinomina, setChiedeRinomina] = useState(false);

  /* L'anagrafica arriva dalla LISTA con i soli campi pubblici, non da
     `GET /wallets/:id`: quella rotta porta con sé `encryptedPrivateKey`. */
  const elenco = usaRichiesta(
    () => wallets.elenco({ fields: CAMPI_WALLET_PUBBLICI, sort: ['id,ASC'] }),
    [],
  );
  const wallet = useMemo(
    () => (elenco.dati?.data ?? []).map(soloDatiPubblici).find((w) => w.id === id) ?? null,
    [elenco.dati, id],
  );

  const saldi = usaRichiesta(
    (segnale) => wallets.saldiTutteLeCatene(id, { refresh: rilettura > 0 }, segnale),
    [id, rilettura],
  );

  /* L'ora che conta è quella della lettura ON-CHAIN, non quella della risposta
     HTTP: il backend può servire una scansione di quaranta secondi fa. */
  const letturaA = saldi.dati?.scannedAt ? new Date(saldi.dati.scannedAt) : saldi.letturaA;
  const stantio = eStantio(letturaA);

  const catene = useMemo(() => ordinaCatene(saldi.dati?.chains ?? []), [saldi.dati]);
  const catenaAttiva = useMemo(() => {
    if (catenaScelta !== 'tutte') return catene.find((c) => String(c.chainId) === catenaScelta) ?? null;
    /* Con "tutte" selezionata il controllo di liquidità e le transazioni hanno
       comunque bisogno di UNA catena: si prende quella dove ci sono più soldi,
       e lo si scrive. */
    return catene.find((c) => c.ok) ?? catene[0] ?? null;
  }, [catene, catenaScelta]);

  const transazioni = usaRichiesta(
    (segnale) => wallets.transazioni(id, catenaAttiva!.chainId, { limit: 25 }, segnale),
    [id, catenaAttiva?.chainId],
    {
      attiva: catenaAttiva != null,
      eVuoto: (d) => d.transactions.length === 0,
    },
  );

  const schede: VoceScheda[] = [
    { chiave: 'tutte', etichetta: 'Tutte', nota: `${catene.length}` },
    ...catene.map((c) => ({
      chiave: String(c.chainId),
      etichetta: c.name,
      nota: c.ok ? (c.totalUsd == null ? 'n/d' : valuta(c.totalUsd, '$', 0)) : 'muta',
      attenzione: !c.ok || c.gasAlert != null,
    })),
  ];

  const catenaVisibili = catenaScelta === 'tutte' ? catene : catene.filter((c) => String(c.chainId) === catenaScelta);
  const totale = saldi.dati?.totalUsd;
  const catenePerse = (saldi.dati?.totalChains ?? 0) - (saldi.dati?.okChains ?? 0);

  async function elimina() {
    setInEliminazione(true);
    try {
      await wallets.elimina(id);
      setChiedeEliminazione(false);
      naviga('/wallet', { replace: true });
    } catch {
      mostra('Eliminazione non riuscita: il wallet è ancora al suo posto.', 'fallito');
      setInEliminazione(false);
    }
  }

  return (
    <div className="sb-cornice">
      <Avvisi avvisi={avvisi} />

      <IntestazionePagina
        titolo={wallet?.nome ?? `Wallet #${id}`}
        sotto={
          <div className="sb-colonna" style={{ gap: 'var(--sb-s-3)' }}>
            <div className="sb-riga" style={{ gap: 'var(--sb-s-3)' }}>
              {/* Indirizzo troncato: basta a riconoscere il wallet, non basta a
                  trascriverlo per sbaglio in una chat o in uno screenshot. */}
              <span className="sb-nota">{troncaIndirizzo(wallet?.indirizzo)}</span>
              <Pulsante
                variante="nudo"
                onClick={async () => {
                  const fatto = await copiaIndirizzo(wallet?.indirizzo ?? null);
                  mostra(
                    fatto ? 'Indirizzo pubblico copiato negli appunti.' : 'Copia non riuscita.',
                    fatto ? 'riuscito' : 'fallito',
                  );
                }}
              >
                Copia indirizzo
              </Pulsante>
              <Pulsante variante="nudo" onClick={() => setChiedeRinomina(true)}>
                Rinomina
              </Pulsante>
              <Link to="/wallet" className="sb-nota" style={{ color: 'var(--sb-accento-chiaro)' }}>
                tutti i wallet
              </Link>
            </div>

            <div className="sb-w-totale">
              <span className="sb-w-totale__valore">
                {totale == null ? 'n/d' : valuta(totale, '$', 2)}
              </span>
              <span className="sb-nota">
                {letturaA ? `letto alle ${ora(letturaA)} — ${daQuanto(letturaA)}` : 'mai letto'}
                {saldi.dati?.cached ? ' · dalla cache del server' : ''}
              </span>
            </div>

            {catenePerse > 0 && (
              <span className="sb-nota" style={{ color: 'var(--sb-cautela-testo)' }}>
                totale parziale: {catenePerse} {catenePerse === 1 ? 'catena non ha risposto' : 'catene non hanno risposto'}
              </span>
            )}
          </div>
        }
        azioni={
          <Pulsante
            variante="tenue"
            onClick={() => setRilettura((r) => r + 1)}
            disabled={saldi.inAggiornamento || saldi.stato === 'caricamento'}
          >
            {saldi.inAggiornamento ? 'Lettura in corso…' : 'Rileggi dalle catene'}
          </Pulsante>
        }
      />

      <div style={{ padding: 'var(--sb-pad-pagina)' }}>
        <StrisciaSchede
          voci={schede}
          attiva={catenaScelta}
          onScegli={setCatenaScelta}
          etichetta="Catene del wallet"
        />

        {stantio && letturaA && (
          <div style={{ marginTop: 'var(--sb-s-6)' }}>
            <StatoStantio letturaA={letturaA} onAggiorna={() => setRilettura((r) => r + 1)}>
              {totale == null ? 'n/d' : valuta(totale, '$', 2)}
            </StatoStantio>
          </div>
        )}

        <div className="sb-w-colonne" style={{ marginTop: 'var(--sb-s-7)' }}>
          <div className="sb-w-colonna">
            <ContenitoreDati
              stato={saldi.stato}
              causaVuoto="Nessuna catena ha restituito saldi per questo indirizzo."
              messaggioErrore={saldi.errore?.message}
              onRiprova={saldi.ricarica}
              scheletro={<ScheletroLista righe={4} />}
            >
              {catenaVisibili.map((c) => (
                <SchedaCatena key={c.chainId} catena={c} indirizzo={saldi.dati?.address ?? null} />
              ))}
            </ContenitoreDati>

            {catenaAttiva && (
              <>
                <Transazioni
                  attivita={transazioni.dati}
                  stato={transazioni.stato}
                  nomeCatena={
                    catenaScelta === 'tutte'
                      ? `${catenaAttiva.name} — la catena con più valore fra quelle lette`
                      : catenaAttiva.name
                  }
                  messaggioErrore={transazioni.errore?.message}
                  onRiprova={transazioni.ricarica}
                />
                <RicaricaTransazioni
                  onRicarica={transazioni.ricarica}
                  inCorso={transazioni.inAggiornamento || transazioni.stato === 'caricamento'}
                />
              </>
            )}
          </div>

          <div className="sb-w-colonna">
            {/* Chi opera qui sopra, PRIMA del controllo di liquidità: il
                preventivo serve a decidere se impegnare capitale, e la domanda
                che viene prima è se su questo indirizzo c'è già qualcosa che
                compra e vende da solo. */}
            <BotDelWallet walletId={id} />

            <ControlloLiquidita
              catena={catenaAttiva}
              letturaA={letturaA}
              stantio={stantio}
              inCaricamento={saldi.stato === 'caricamento'}
            />
          </div>
        </div>

        {/* --- in fondo, dove arriva il pollice: quello che non si annulla --- */}
        <div className="sb-w-fondo">
          <span className="sb-occhiello">Azioni irreversibili</span>
          <div className="sb-colonna" style={{ marginTop: 'var(--sb-s-5)', maxWidth: 520 }}>
            <Avvertenza livello="cautela">
              SemBot non ha un endpoint per spostare fondi: da qui non si preleva e non si invia
              nulla. L'unica azione che tocca il denaro è togliere il wallet, e toglie la chiave
              con cui SemBot lo raggiunge.
            </Avvertenza>
            <Pulsante variante="distruttivo" onClick={() => setChiedeEliminazione(true)}>
              Elimina questo wallet…
            </Pulsante>
          </div>
        </div>
      </div>

      <ModuloRinominaWallet
        wallet={chiedeRinomina ? wallet : null}
        onChiudi={() => setChiedeRinomina(false)}
        /* I nomi degli ALTRI wallet: `wallet.name` è UNIQUE a database, e un
           409 dopo l'invio si legge come "non ha funzionato", non come "quel
           nome è già di un altro". */
        nomiEsistenti={(elenco.dati?.data ?? []).map((w) => w.name)}
        onRinominato={(nome) => {
          setChiedeRinomina(false);
          mostra(`Wallet rinominato in "${nome}".`, 'riuscito');
          elenco.ricarica();
        }}
      />

      {/* La conferma ripete l'importo e l'indirizzo: chi conferma deve leggere
          su quale wallet sta agendo, non ricordarselo. */}
      <Modale
        aperta={chiedeEliminazione}
        onChiudi={() => setChiedeEliminazione(false)}
        titolo="Elimina il wallet"
        sottotitolo={wallet?.nome}
        pieda={
          <AzioneDistruttiva
            etichetta="Elimina il wallet"
            etichettaArmata="Tocca di nuovo per eliminare"
            nota="I fondi restano sulla catena, ma SemBot perde la chiave con cui li raggiunge."
            onConferma={elimina}
            disabilitata={inEliminazione}
          />
        }
      >
        <div className="sb-colonna">
          <Scheda variante="incassata">
            <div className="sb-riga">
              <span className="sb-etichetta" style={{ flex: 1 }}>
                Valore a bordo
              </span>
              <span className="sb-w-catena__usd">
                {totale == null ? 'n/d' : valuta(totale, '$', 2)}
              </span>
            </div>
            <hr className="sb-divisore" />
            <div className="sb-riga">
              <span className="sb-etichetta" style={{ flex: 1 }}>
                Indirizzo
              </span>
              <span className="sb-nota">{troncaIndirizzo(wallet?.indirizzo)}</span>
            </div>
            <hr className="sb-divisore" />
            <div className="sb-riga">
              <span className="sb-etichetta" style={{ flex: 1 }}>
                Letto
              </span>
              <span className="sb-nota">{letturaA ? daQuanto(letturaA) : 'mai'}</span>
            </div>
          </Scheda>

          <Avvertenza livello="grave">
            Se ci sono ancora fondi su questo indirizzo, spostali PRIMA di eliminare: dopo, senza la
            chiave, non sono più raggiungibili da SemBot.
          </Avvertenza>

          {/* `letturaA` va verificato come alla riga della testata: il ripiego
              `?? new Date()` avrebbe scritto «di adesso» proprio nell'avviso
              che serve a dire che il numero NON è di adesso. */}
          {stantio && letturaA && (
            <Avvertenza livello="cautela">
              Il valore qui sopra è di {daQuanto(letturaA)}. Rileggi dalle catene prima di decidere.
            </Avvertenza>
          )}
        </div>
      </Modale>
    </div>
  );
}
